import React from 'react'
import { useNavigate } from 'react-router-dom'
import { clearAuth, loadAuth } from '../../lib/authStorage'
import { apiGet } from '../../lib/api'
import { BUSINESS, CONTACTS, USER_ME, USER_PROFILE_COMPLETION } from '../../lib/endpoints'
import { fetchNudgeQueue } from '../../lib/nudgeApi'
import { fetchCurrentSubscription } from '../../lib/billingApi'
import { readProfileCache, writeProfileCache } from '../../lib/profileCache'
import type { BusinessInfo, Contact, ProfileCompletion, UserProfile } from '../../types'
import type { CurrentSubscriptionResponse, FeatureKey, NudgeQueueSummary } from '../../types/api'
import type { DashboardPageName, ThemeMode } from './types'

const QUEUE_POLL_MS = 60_000

type DashboardContextValue = {
  me: UserProfile | null
  businessInfo: BusinessInfo | null
  theme: ThemeMode
  setTheme: React.Dispatch<React.SetStateAction<ThemeMode>>
  contacts: Contact[]
  contactsLoading: boolean
  loadContacts: () => Promise<void>
  queueRows: NudgeQueueSummary[]
  queueLoading: boolean
  refreshQueue: () => void
  activePage: DashboardPageName
  setActivePage: React.Dispatch<React.SetStateAction<DashboardPageName>>
  profilePendingPercent: number | null
  refreshBusinessInfo: () => Promise<void>
  currentSubscription: CurrentSubscriptionResponse | null
  subscriptionLoading: boolean
  reloadSubscription: () => Promise<void>
  isFeatureAllowed: (key: FeatureKey) => boolean
  getFeatureLimit: (key: FeatureKey) => number
}

const DashboardContext = React.createContext<DashboardContextValue | null>(null)

export function useDashboard(): DashboardContextValue {
  const ctx = React.useContext(DashboardContext)
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider')
  return ctx
}

function loadTheme(): ThemeMode {
  const stored = window.localStorage.getItem('nudge-theme')
  if (stored === 'light' || stored === 'dark') return stored
  return 'light'
}

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const [theme, setTheme] = React.useState<ThemeMode>(() => loadTheme())
  const isAdmin = loadAuth()?.role === 'ADMIN'
  const [activePage, setActivePage] = React.useState<DashboardPageName>(() => {
    if (isAdmin) return 'admin-overview'
    const stored = localStorage.getItem('nudge.activePage')
    return (stored as DashboardPageName) ?? 'dashboard'
  })
  // Hydrate the user/business shell from localStorage so the sidebar can render
  // the real name + avatar instantly on dashboard re-mount. The fresh API calls
  // below will overwrite once they resolve.
  const [me, setMe] = React.useState<UserProfile | null>(() => {
    const cached = readProfileCache()
    if (!cached) return null
    return {
      firstName: cached.firstName ?? null,
      lastName: cached.lastName ?? null,
      email: cached.email ?? null,
      avatarUrl: cached.avatarUrl ?? null,
    }
  })
  const [businessInfo, setBusinessInfo] = React.useState<BusinessInfo | null>(() => {
    const cached = readProfileCache()
    if (!cached?.businessName && !cached?.businessLogoUrl) return null
    return {
      businessName: cached?.businessName ?? null,
      logoUrl: cached?.businessLogoUrl ?? null,
    }
  })
  const [contacts, setContacts] = React.useState<Contact[]>([])
  const [contactsLoading, setContactsLoading] = React.useState(false)
  const [profilePendingPercent, setProfilePendingPercent] = React.useState<number | null>(null)
  const [queueRows, setQueueRows] = React.useState<NudgeQueueSummary[]>([])
  const [queueLoading, setQueueLoading] = React.useState(false)
  const [currentSubscription, setCurrentSubscription] = React.useState<CurrentSubscriptionResponse | null>(null)
  const [subscriptionLoading, setSubscriptionLoading] = React.useState(false)

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    window.localStorage.setItem('nudge-theme', theme)
  }, [theme])

  React.useEffect(() => {
    localStorage.setItem('nudge.activePage', activePage)
  }, [activePage])

  React.useEffect(() => {
    const pageNames: Record<string, string> = {
      dashboard:          'Dashboard',
      contacts:           'Contacts',
      lapsed:             'Lapsed',
      recovered:          'Recovered',
      messages:           'Messages',
      reports:            'Reports',
      business:           'Business',
      services:           'Offerings',
      settings:           'Settings',
      profile:            'Profile',
      'admin-metrics':    'Metrics',
      'admin-slots':      'Scheduler',
      'admin-billing':    'Billing',
      'admin-businesses': 'Businesses',
      'admin-ai-health':  'AI Health',
      'admin-overview':   'Overview',
    }
    const name = pageNames[activePage] ?? 'Dashboard'
    document.title = `${name} | Nudge`
    return () => { document.title = 'Nudge' }
  }, [activePage])

  // Manrope + DM Mono are loaded globally in index.html — no dynamic font injection needed

  React.useEffect(() => {
    let cancelled = false

    async function loadMe() {
      if (!loadAuth()?.accessToken) {
        clearAuth()
        navigate('/signin', { replace: true })
        return
      }

      try {
        const meData = await apiGet<UserProfile>(USER_ME)
        if (cancelled) return
        setMe(meData)
        writeProfileCache({
          firstName: meData.firstName ?? null,
          lastName: meData.lastName ?? null,
          email: meData.email ?? null,
          avatarUrl: meData.avatarUrl ?? null,
          isAdmin,
        })

        if (!isAdmin) {
          const [completion, bizData] = await Promise.all([
            apiGet<ProfileCompletion>(USER_PROFILE_COMPLETION).catch(() => null),
            apiGet<BusinessInfo>(BUSINESS).catch(() => null),
          ])
          if (cancelled) return
          if (completion) {
            setProfilePendingPercent(
              typeof completion.profileCompletionPercent === 'number'
                ? completion.profileCompletionPercent
                : null
            )
          }
          if (bizData) {
            setBusinessInfo(bizData)
            writeProfileCache({
              businessName: bizData.businessName ?? null,
              businessLogoUrl: bizData.logoUrl ?? null,
            })
          }
        }
      } catch (e) {
        if ((e as { status?: number }).status === 401 || (e as { status?: number }).status === 403) {
          clearAuth()
          navigate('/signin', { replace: true })
          return
        }
        if (!cancelled) setMe(null)
      }
    }

    loadMe()
    return () => {
      cancelled = true
    }
  }, [navigate])

  const refreshBusinessInfo = React.useCallback(async () => {
    if (!loadAuth()?.accessToken || isAdmin) return
    try {
      const [completion, bizData] = await Promise.all([
        apiGet<ProfileCompletion>(USER_PROFILE_COMPLETION).catch(() => null),
        apiGet<BusinessInfo>(BUSINESS).catch(() => null),
      ])
      if (completion) {
        setProfilePendingPercent(
          typeof completion.profileCompletionPercent === 'number'
            ? completion.profileCompletionPercent
            : null
        )
      }
      if (bizData) {
        setBusinessInfo(bizData)
        writeProfileCache({
          businessName: bizData.businessName ?? null,
          businessLogoUrl: bizData.logoUrl ?? null,
        })
      }
    } catch {
      // keep existing data on error
    }
  }, [isAdmin])

  const loadContacts = React.useCallback(async () => {
    if (!loadAuth()?.accessToken || isAdmin) return
    try {
      setContactsLoading(true)
      const data = await apiGet<Contact[]>(CONTACTS)
      setContacts(data)
    } catch {
      // Keep existing contacts on error
    } finally {
      setContactsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadContacts()
  }, [loadContacts])

  React.useEffect(() => {
    if (activePage !== 'contacts') return
    loadContacts()
  }, [activePage, loadContacts])

  const loadQueue = React.useCallback(async () => {
    if (!loadAuth()?.accessToken || isAdmin) return
    setQueueLoading(true)
    try {
      const data = await fetchNudgeQueue(0, 200)
      setQueueRows(data.content)
    } catch {
      // keep existing rows on error
    } finally {
      setQueueLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadQueue()
    const timer = setInterval(loadQueue, QUEUE_POLL_MS)
    return () => clearInterval(timer)
  }, [loadQueue])

  const reloadSubscription = React.useCallback(async () => {
    if (!loadAuth()?.accessToken || isAdmin) return
    setSubscriptionLoading(true)
    try {
      const data = await fetchCurrentSubscription()
      setCurrentSubscription(data)
    } catch {
      // keep existing data on error
    } finally {
      setSubscriptionLoading(false)
    }
  }, [])

  React.useEffect(() => {
    reloadSubscription()
  }, [reloadSubscription])

  React.useEffect(() => {
    function handlePlanWarning() { void reloadSubscription() }
    window.addEventListener('nudge:plan-warning', handlePlanWarning)
    return () => window.removeEventListener('nudge:plan-warning', handlePlanWarning)
  }, [reloadSubscription])

  const isFeatureAllowed = React.useCallback(
    (key: FeatureKey): boolean => {
      if (!currentSubscription) return true
      const entitlement = currentSubscription.entitlements[key]
      if (!entitlement) return true
      if (entitlement.type === 'BOOL') return entitlement.value
      return entitlement.value > 0 || entitlement.value === -1
    },
    [currentSubscription],
  )

  const getFeatureLimit = React.useCallback(
    (key: FeatureKey): number => {
      if (!currentSubscription) return -1
      const entitlement = currentSubscription.entitlements[key]
      if (!entitlement || entitlement.type !== 'INT') return -1
      return entitlement.value
    },
    [currentSubscription],
  )

  return (
    <DashboardContext.Provider
      value={{
        me,
        businessInfo,
        theme,
        setTheme,
        contacts,
        contactsLoading,
        loadContacts,
        queueRows,
        queueLoading,
        refreshQueue: loadQueue,
        activePage,
        setActivePage,
        profilePendingPercent,
        refreshBusinessInfo,
        currentSubscription,
        subscriptionLoading,
        reloadSubscription,
        isFeatureAllowed,
        getFeatureLimit,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}
